import { useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import Frame from './assets/frame'
import Person from './assets/person'
import {
  buildPhaseLoop,
  AnimationComponentProps as Props,
} from './phaseAnimation'

const REFERENCE_SIZE = 24

const FRAME_SIZE = [24, 18, 18]
const FRAME_ROTATE = [0, 15, -15]
const FRAME_OPACITY = [0.25, 1, 1]

const PERSON_SIZE = [10, 10, 10]
const PERSON_ROTATE = [0, -30, 15]

const PHASE_DURATIONS = [800, 600, 600]
const PHASE_DELAYS = [0, 400, 0]
const PHASE_EASINGS = [
  Easing.inOut(Easing.ease),
  Easing.inOut(Easing.ease),
  Easing.inOut(Easing.ease),
]

export default function PersonFrameRotate({
  size = REFERENCE_SIZE,
  color = colors.white,
  durations = PHASE_DURATIONS,
  delays = PHASE_DELAYS,
  easings = PHASE_EASINGS,
  isActive = true,
}: Props) {
  const scale = size / REFERENCE_SIZE

  const frameSize = useSharedValue(FRAME_SIZE[0] * scale)
  const frameRotate = useSharedValue(FRAME_ROTATE[0])
  const frameOpacity = useSharedValue(FRAME_OPACITY[0])
  const personSize = useSharedValue(PERSON_SIZE[0] * scale)
  const personRotate = useSharedValue(PERSON_ROTATE[0])

  useEffect(() => {
    if (!isActive) {
      cancelAnimation(frameSize)
      cancelAnimation(frameRotate)
      cancelAnimation(frameOpacity)
      cancelAnimation(personSize)
      cancelAnimation(personRotate)
      return
    }

    frameSize.set(
      buildPhaseLoop(
        FRAME_SIZE.map((value) => value * scale),
        durations,
        delays,
        easings,
        true
      )
    )

    frameRotate.set(
      buildPhaseLoop(FRAME_ROTATE, durations, delays, easings, true)
    )

    frameOpacity.set(
      buildPhaseLoop(FRAME_OPACITY, durations, delays, easings, true)
    )

    personSize.set(
      buildPhaseLoop(
        PERSON_SIZE.map((value) => value * scale),
        durations,
        delays,
        easings,
        true
      )
    )

    personRotate.set(
      buildPhaseLoop(PERSON_ROTATE, durations, delays, easings, true)
    )
  }, [
    isActive,
    scale,
    durations,
    delays,
    easings,
    frameSize,
    frameRotate,
    frameOpacity,
    personSize,
    personRotate,
  ])

  const frameStyle = useAnimatedStyle(() => ({
    width: frameSize.value,
    height: frameSize.value,
    opacity: frameOpacity.value,
    transform: [{ rotate: `${frameRotate.value}deg` }],
  }))

  const personStyle = useAnimatedStyle(() => ({
    width: personSize.value,
    height: personSize.value,
    transform: [{ rotate: `${personRotate.value}deg` }],
  }))

  return (
    <View
      style={{ width: size, height: size }}
      className="items-center justify-center"
    >
      <Animated.View className="absolute" style={frameStyle}>
        <Frame size="100%" color={color} strokeWidth={2} />
      </Animated.View>
      <Animated.View className="absolute" style={personStyle}>
        <Person size="100%" color={color} />
      </Animated.View>
    </View>
  )
}
